import { Injectable, Renderer2, RendererFactory2, NgZone } from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';

export interface NcMouseTooltipOptions {
  offsetX?: number;
  offsetY?: number;
  styles?: { [key: string]: string };
}

@Injectable()
export class NcMouseTooltipService {
  private tooltipElement: HTMLElement | null = null;
  private renderer: Renderer2;
  private offset = { x: 12, y: 12 };
  private mouseMoveSubscription: Subscription | null = null;
  private mousePosition = { x: 0, y: 0 };
  private defaultStyles: { [key: string]: string } = {
    'position': 'fixed',
    'padding': '8px 12px',
    'background': 'rgba(0, 0, 0, 0.8)',
    'color': 'white',
    'border-radius': '4px',
    'font-size': '14px',
    'z-index': '10001',
    'pointer-events': 'none',
    'transition': 'none'
  };

  constructor(
    private rendererFactory: RendererFactory2,
    private ngZone: NgZone
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * 初始化服务
   */
  public init(): void {
    if (this.mouseMoveSubscription) {
      this.mouseMoveSubscription.unsubscribe();
      this.mouseMoveSubscription = null;
    }

    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveSubscription = fromEvent<MouseEvent>(document, 'mousemove')
        .subscribe((event: MouseEvent) => {
          // 更新鼠标位置
          this.mousePosition = { x: event.clientX, y: event.clientY };

          // 更新位置
          if (this.tooltipElement) {
            this.updatePosition();
          }
        });
    });
  }

  /**
   * 显示tooltip
   * @param content tooltip内容
   * @param options 可选配置，包括x、y偏移量和样式
   */
  public show(content: string, options?: NcMouseTooltipOptions): void {
    if (options?.offsetX !== undefined) {
      this.offset.x = options.offsetX;
    } else {
      this.offset.x = 12;
    }

    if (options?.offsetY !== undefined) {
      this.offset.y = options.offsetY;
    } else {
      this.offset.y = 12;
    }

    if (!this.tooltipElement) {
      this.createTooltipElement(options?.styles);
    } else {
      if (options?.styles) {
        this.updateStyles(options.styles);
      } else {
        this.updateStyles(this.defaultStyles);
      }
    }

    if (this.tooltipElement) {
      this.renderer.setProperty(this.tooltipElement, 'innerHTML', content);
      this.updatePosition();
    }
  }

  /**
   * 隐藏tooltip
   */
  public hide(): void {
    if (this.tooltipElement) {
      document.body.removeChild(this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  /**
   * 销毁
   */
  public destroy(): void {
    this.hide();
    if (this.mouseMoveSubscription) {
      this.mouseMoveSubscription.unsubscribe();
      this.mouseMoveSubscription = null;
    }
  }

  /**
   * 创建tooltip元素
   * @param customStyles 自定义样式
   */
  private createTooltipElement(customStyles?: { [key: string]: string }): void {
    if (this.tooltipElement) {
      return;
    }

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'nc-mouse-tooltip');

    // 应用默认样式
    this.updateStyles(this.defaultStyles);

    // 应用自定义样式（如果提供）
    if (customStyles) {
      this.updateStyles(customStyles);
    }

    this.renderer.appendChild(document.body, this.tooltipElement);
  }

  /**
   * 更新tooltip位置
   */
  private updatePosition(): void {
    if (!this.tooltipElement) {
      return;
    }

    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 计算初始位置（鼠标右下方）
    let left = this.mousePosition.x + this.offset.x;
    let top = this.mousePosition.y + this.offset.y;

    // 检查右边界
    if (left + tooltipRect.width > viewportWidth) {
      // 如果超出右边界，将tooltip放在鼠标左侧
      left = this.mousePosition.x - tooltipRect.width - this.offset.x;
    }

    // 检查下边界
    if (top + tooltipRect.height > viewportHeight) {
      // 如果超出下边界，将tooltip放在鼠标上方
      top = this.mousePosition.y - tooltipRect.height - this.offset.y;
    }

    // 检查左边界
    if (left < 0) {
      left = this.offset.x;
    }

    // 检查上边界
    if (top < 0) {
      top = this.offset.y;
    }

    // 设置位置
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
  }

  /**
   * 更新tooltip样式
   * @param customStyles 自定义样式
   */
  private updateStyles(customStyles: { [key: string]: string }): void {
    if (!this.tooltipElement) {
      return;
    }

    Object.entries(customStyles).forEach(([property, value]) => {
      this.renderer.setStyle(this.tooltipElement, property, value);
    });
  }
}

